/**
 * Recovery for MSAL's single global "interaction in progress" lock.
 *
 * MSAL allows one interactive request (popup or redirect) at a time, tracked by
 * a flag at `msal.interaction.status`. Any interactive call made while that flag
 * is set throws BrowserAuthError `interaction_in_progress` — the raw
 * "Interaction is currently in progress..." message users were seeing on the
 * weekly check-in page.
 *
 * The flag lives in **sessionStorage**, so it is per-tab and gets cleared on the
 * next page load by handleRedirectPromise() -> resetRequestCache().
 *
 * ## Why this module exists, and what changed in MSAL v5
 *
 * Under v4 there was a trap. Our `cacheLocation` is `localStorage`, and v4's
 * BrowserCacheManager.getTemporaryCache() fell back to reading temporary keys
 * from localStorage when sessionStorage had no copy — a compatibility path for
 * "items set by old versions", which stored temporary items in the configured
 * cacheLocation. v4 never *wrote* there, and removeTemporaryItem() /
 * resetRequestCache() only cleared sessionStorage. So a leftover
 * `msal.interaction.status` in localStorage — written by an MSAL v2/v3 during an
 * interaction that never completed — was a permanent poison pill: MSAL read it
 * forever, every interactive token request threw, and neither a reload, a
 * sign-out, nor a fresh sign-in could clear it. Only wiping site data did.
 *
 * **v5 removed that fallback.** It pins the temporary cache to sessionStorage
 * unconditionally (`temporaryCacheStorage = getStorageImplementation(clientId,
 * BrowserCacheLocation.SessionStorage, ...)`) and getTemporaryCache() reads only
 * from it, so a localStorage orphan is now inert and the poison pill cannot
 * recur. v5 also self-heals a *malformed* flag: the value is now
 * `JSON.stringify({ clientId, type })` rather than a bare string, and
 * getInteractionInProgress() removes the key and calls resetRequestCache() when
 * JSON.parse fails — which is what happens to any v4-era value left behind.
 *
 * So under v5 purgeLegacyInteractionLock() is **hygiene, not a fix**: the
 * orphans are real and worth clearing out of users' profiles, but they are no
 * longer capable of wedging anything. clearStaleInteractionLock() is still a
 * genuine recovery path — it removes the key outright, which works regardless of
 * whether the value is v4's string or v5's JSON.
 *
 * Both touch only MSAL's temporary keys — never accounts, tokens, or the key
 * indexes. Note v5 renamed those indexes to include the cache schema version
 * (`msal.3.account.keys`, `msal.3.token.keys.<clientId>`); the suffix list below
 * does not match them, and a test pins that.
 */

// MSAL's TemporaryCacheKeys. The interaction flag is stored unprefixed
// (`msal.interaction.status`); the rest are client-scoped
// (`msal.<clientId>.request.params`). Verified unchanged in msal-browser 5.19.0
// — if a future version renames one, the orphan for that key stops being
// cleaned rather than anything breaking loudly.
const TEMP_KEY_SUFFIXES = [
  'interaction.status',
  'request.origin',
  'urlHash',
  'request.params',
  'code.verifier',
  'request.native',
];

const INTERACTION_STATUS_KEY = 'msal.interaction.status';

function isTemporaryKey(key) {
  return key.startsWith('msal.')
    && TEMP_KEY_SUFFIXES.some(suffix => key === `msal.${suffix}` || key.endsWith(`.${suffix}`));
}

// Storage can throw (Safari private mode, blocked third-party contexts). Auth
// must never break because a cleanup read failed.
function safeKeys(storage) {
  try {
    return Object.keys(storage);
  } catch {
    return [];
  }
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Removes MSAL temporary-cache orphans from localStorage. Call once at startup,
 * after initialize() but BEFORE handleRedirectPromise() — a genuine in-flight
 * redirect records its state in sessionStorage, so nothing here can belong to a
 * live interaction.
 *
 * @returns {string[]} the keys that were removed (for logging/telemetry)
 */
export function purgeLegacyInteractionLock() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  const removed = [];
  for (const key of safeKeys(window.localStorage)) {
    if (isTemporaryKey(key) && safeRemove(window.localStorage, key)) removed.push(key);
  }
  return removed;
}

/**
 * Clears the interaction flag from both storages. Call only when the caller
 * knows no interaction is actually running (see msalTokenManager) — MSAL cannot
 * clear an orphan itself, and without this the tab stays wedged until reload.
 *
 * @returns {boolean} true if a flag was present in either storage
 */
export function clearStaleInteractionLock() {
  if (typeof window === 'undefined') return false;
  let found = false;
  for (const storage of [window.sessionStorage, window.localStorage]) {
    if (!storage) continue;
    try {
      if (storage.getItem(INTERACTION_STATUS_KEY) !== null) found = true;
    } catch {
      continue;
    }
    safeRemove(storage, INTERACTION_STATUS_KEY);
  }
  return found;
}
